import { ModalEntityType } from "@/components/LinkAsModal";
import { ListItemKind, MakerResponse, TeamResponse } from "@/web-client/api";
import { ParsedUrlQuery } from "querystring";
import { useCallback, useMemo } from "react";
import { FavoriteItemResponse } from "@/types/FavoriteItemResponse";

type UseListChildItemResponse = {
  entityId: number;
  entityType: ModalEntityType;
  itemHref: string;
  authorHref?: string;
  authorIconSrc: string;
  type: string;
  params?: ParsedUrlQuery;
};

const useListChildItem = (
  listItem: FavoriteItemResponse,
): UseListChildItemResponse => {
  const authorIsMaker = useMemo<boolean>(() => {
    return "is_contracted" in listItem.author;
  }, [listItem]);

  const toProject = useCallback<() => UseListChildItemResponse>(() => {
    const team = listItem.author as TeamResponse;

    return {
      entityId: listItem.detailable_id.project_id,
      entityType: ModalEntityType.Project,
      itemHref: `/projects/${listItem.detailable_id.project_id}`,
      authorHref: `/teams/${team.id}/projects`,
      type: "プロジェクト",
      authorIconSrc: team.logo?.urls?.small || "/imgs/default_team_icon.png",
    };
  }, [listItem]);

  const toPost = useCallback<() => UseListChildItemResponse>(() => {
    const base = {
      entityId: listItem.detailable_id.post_id,
      entityType: ModalEntityType.Post,
      itemHref: `/posts/${listItem.detailable_id.post_id}`,
      type: "写真",
    };

    if (authorIsMaker) {
      const maker = listItem.author as MakerResponse;

      return {
        ...base,
        authorHref: `/makers/${maker.id}`,
        authorIconSrc:
          maker.icon?.urls?.small || "/imgs/default_maker_icon.png",
      };
    }

    const team = listItem.author as TeamResponse;

    return {
      ...base,
      authorHref: `/teams/${team.id}/projects`,
      authorIconSrc: team.logo?.urls?.small || "/imgs/default_team_icon.png",
    };
  }, [listItem, authorIsMaker]);

  const toProduct = useCallback<() => UseListChildItemResponse>(() => {
    const maker = listItem.author as MakerResponse;

    return {
      entityId: listItem.detailable_id.product_id,
      entityType: ModalEntityType.Product,
      authorHref: maker.is_contracted ? `/makers/${maker.id}` : undefined,
      authorIconSrc: maker.icon?.urls?.small || "/imgs/default_maker_icon.png",
      itemHref: `/products/${listItem.detailable_id.product_id}`,
      type: listItem.kind === ListItemKind.BUILDING_MATERIAL ? "建材" : "家具",
      params: { v_id: listItem.detailable_id.product_variation_id.toString() },
    };
  }, [listItem]);

  const response = useMemo<UseListChildItemResponse>(() => {
    switch (listItem.kind) {
      case ListItemKind.PROJECT:
        return toProject();
      case ListItemKind.POST:
        return toPost();
      default:
        return toProduct();
    }
  }, [listItem, toProject, toPost, toProduct]);

  return response;
};
export default useListChildItem;
